/**
 * Authentication service
 */

import apiClient, { _clearPersistentCache, _clearSharedGetCache } from './api';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import {
  clearLoginMark,
  kickPostLoginWarmup,
  markLoginCompleted,
} from './postLoginWarmup';
import {
  clearRiskLocalCaches,
  deriveSessionScope,
  notifySessionScopeChanged,
} from '../utils/sessionScope';
import { clearLegacyUnscopedFlightStorage } from '../utils/flightStorageScope';

const LAST_TENANT_SCHEMA_KEY = 'aviatto:lastTenantSchema';
const LOGIN_REQUEST_TIMEOUT_MS = 25000;
// 3 attempts (initial + 2 retries) with 700ms / 1400ms backoff lets the SPA
// ride through a brief ECS rolling-deploy cold-start window without surfacing
// "Erro ao tentar entrar. Tente novamente em instantes." to the user. The
// retries are gated by `_isTransientLoginError`, so a real 401/400 still fails
// fast on the first attempt and never burns the extra round-trips.
const LOGIN_MAX_TRANSIENT_ATTEMPTS = 3;

// Security migration: the former `gendec_<flightId>` and checklist keys were
// impossible to attribute safely because database IDs repeat across tenant
// schemas. New writes are fully session-scoped; discard ambiguous legacy
// payloads as soon as the auth layer boots.
clearLegacyUnscopedFlightStorage();

// Only attempt the developer-only "local Django" fallback when the SPA is
// actually being served from localhost / 127.0.0.1. In production this
// fallback would always fail with ERR_CONNECTION_REFUSED and the resulting
// network error (which has no `.response`) used to mask a clean 401 from
// the real backend, surfacing as the misleading "Erro ao tentar entrar.
// Tente novamente em instantes." instead of "wrong username/password".
function _isRunningOnLocalhost() {
  try {
    if (typeof window === 'undefined' || !window.location) return false;
    const h = window.location.hostname;
    return h === 'localhost' || h === '127.0.0.1';
  } catch {
    return false;
  }
}

function _decodeAccessToken(token = '') {
  try {
    const raw = token || localStorage.getItem('access_token');
    if (!raw) return null;
    return jwtDecode(raw);
  } catch {
    return null;
  }
}

function _profileMatchesToken(profile, decoded) {
  if (!profile || typeof profile !== 'object' || !decoded) return false;

  const tokenUserId = decoded?.user_id || decoded?.id || decoded?.sub || '';
  const profileUserId = profile?.id || profile?.user_id || '';
  if (tokenUserId && profileUserId && String(tokenUserId) !== String(profileUserId)) {
    return false;
  }

  const tokenTenantSchema = String(decoded?.tenant_schema || decoded?.schema_name || '').trim();
  const profileTenantSchema = String(profile?.tenant_schema || profile?.schema_name || '').trim();
  if (tokenTenantSchema && profileTenantSchema !== tokenTenantSchema) {
    return false;
  }

  const tokenCompanyId = String(decoded?.company_id || decoded?.companyId || '').trim();
  const profileCompanyId = String(profile?.company_id || profile?.companyId || profile?.company?.id || '').trim();
  if (tokenCompanyId && profileCompanyId && tokenCompanyId !== profileCompanyId) {
    return false;
  }

  const tokenUsername = String(decoded?.username || '').trim().toLowerCase();
  const profileUsername = String(profile?.username || '').trim().toLowerCase();
  if (tokenUsername && profileUsername && tokenUsername !== profileUsername) {
    return false;
  }

  const tokenEmail = String(decoded?.email || '').trim().toLowerCase();
  const profileEmail = String(profile?.email || '').trim().toLowerCase();
  if (tokenEmail && profileEmail && tokenEmail !== profileEmail) {
    return false;
  }

  return true;
}

function _enrichProfileWithTokenScope(profile, decoded) {
  const enriched = { ...(profile || {}) };
  const tenantSchema = String(decoded?.tenant_schema || decoded?.schema_name || '').trim();
  if (tenantSchema && !enriched.tenant_schema) {
    enriched.tenant_schema = tenantSchema;
  }
  [
    'username',
    'email',
    'full_name',
    'role',
    'company_id',
    'company_name',
    'is_aviato_admin',
    'is_platform_owner',
  ].forEach((key) => {
    if ((enriched[key] === undefined || enriched[key] === null || enriched[key] === '') && decoded?.[key] !== undefined) {
      enriched[key] = decoded[key];
    }
  });
  return enriched;
}

function _rememberTenantSchema(value = '') {
  const tenantSchema = String(value || '').trim();
  if (!tenantSchema) return;
  try {
    localStorage.setItem(LAST_TENANT_SCHEMA_KEY, tenantSchema);
  } catch {
    // best-effort browser hint only
  }
}

function _isStorageQuotaError(error) {
  const name = String(error?.name || '');
  const code = Number(error?.code || 0);
  return (
    name === 'QuotaExceededError'
    || name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || code === 22
    || code === 1014
  );
}

function _recoverBrowserStorageQuota() {
  try {
    _clearSharedGetCache();
    _clearPersistentCache();
  } catch {
    // best-effort cache cleanup only
  }
}

function _setStorageItemWithQuotaRecovery(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    if (!_isStorageQuotaError(error)) throw error;
    _recoverBrowserStorageQuota();
    localStorage.setItem(key, value);
  }
}

function _removeStorageItemBestEffort(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore storage failures
  }
}

function _clearScopedRiskCachesBeforeSessionChange() {
  try {
    clearRiskLocalCaches({ includeLegacy: false });
  } catch {
    // Risk caches are best-effort and must never block authentication.
  }
}

function _notifySessionScopeChangedBestEffort() {
  try {
    notifySessionScopeChanged();
  } catch {
    // Session installation remains authoritative even if listeners fail.
  }
}

function _sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function _isTransientLoginError(error) {
  const status = Number(error?.response?.status || 0);
  if (status === 502 || status === 503 || status === 504) return true;
  if (error?.response) return false;
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '').toLowerCase();
  return (
    code === 'ECONNABORTED'
    || code === 'ERR_NETWORK'
    || message.includes('timeout')
    || message.includes('network error')
  );
}

async function _postLoginWithTransientRetry(loginPayload, loginHeaders) {
  let lastError = null;
  for (let attempt = 0; attempt < LOGIN_MAX_TRANSIENT_ATTEMPTS; attempt += 1) {
    try {
      return await apiClient.post('/auth/login/', loginPayload, {
        timeout: LOGIN_REQUEST_TIMEOUT_MS,
        __skipAuthHeader: true,
        __skipAuthRefresh: true,
        __skipLocalRedirect: true,
        headers: loginHeaders,
      });
    } catch (error) {
      lastError = error;
      if (!_isTransientLoginError(error) || attempt >= LOGIN_MAX_TRANSIENT_ATTEMPTS - 1) {
        throw error;
      }
      await _sleep(700 * (attempt + 1));
    }
  }
  throw lastError;
}

function _writeAuthSession({ access, refresh, decoded, localSession = false }) {
  const write = () => {
    _setStorageItemWithQuotaRecovery('access_token', access);
    _setStorageItemWithQuotaRecovery('refresh_token', refresh);
    _setStorageItemWithQuotaRecovery('user', JSON.stringify(decoded));
    if (localSession) {
      _setStorageItemWithQuotaRecovery('aviatto_local_session', 'true');
    } else {
      _removeStorageItemBestEffort('aviatto_local_session');
    }
  };

  try {
    write();
  } catch (error) {
    if (!_isStorageQuotaError(error)) throw error;
    _recoverBrowserStorageQuota();
    write();
  }
}

function _storeCurrentProfile(profile, accessToken = '') {
  const decoded = _decodeAccessToken(accessToken);
  if (!_profileMatchesToken(profile, decoded)) {
    _removeStorageItemBestEffort('user_profile');
    _notifySessionScopeChangedBestEffort();
    return null;
  }
  const enriched = _enrichProfileWithTokenScope(profile, decoded);
  const previousScope = deriveSessionScope();
  const nextScope = deriveSessionScope({
    profile: enriched,
    user: decoded,
    tokenClaims: decoded,
    accessToken,
  });
  if (
    previousScope.isScoped
    && nextScope.isScoped
    && (
      previousScope.companyScopeId !== nextScope.companyScopeId
      || previousScope.userId !== nextScope.userId
    )
  ) {
    try {
      clearRiskLocalCaches();
    } catch {
      // Risk-cache cleanup must never block a company/user scope change.
    }
  }
  try {
    _setStorageItemWithQuotaRecovery('user_profile', JSON.stringify(enriched));
  } catch (error) {
    console.warn('[authService] Could not persist profile; continuing with token profile.', error);
  }
  _rememberTenantSchema(enriched.tenant_schema || enriched.schema_name);
  _notifySessionScopeChangedBestEffort();
  return enriched;
}

function _rehydrateSyncCacheBestEffort() {
  try {
    if (typeof apiClient.rehydrateSyncCache === 'function') {
      apiClient.rehydrateSyncCache();
    }
  } catch {
    // Hydration is best-effort; a failure just falls back to the async cache path.
  }
}

function _refreshProfileInBackground(accessToken = '', label = 'session') {
  try {
    const expectedAccess = String(accessToken || '');
    apiClient.get('/users/profile/', { __skipRetry: true })
      .then((response) => {
        const currentAccess = localStorage.getItem('access_token') || '';
        if (expectedAccess && currentAccess && currentAccess !== expectedAccess) {
          return;
        }
        if (response?.data) {
          _storeCurrentProfile(response.data, currentAccess || expectedAccess);
          _rehydrateSyncCacheBestEffort();
        }
      })
      .catch((e) => {
        console.warn(`[authService] Failed to refresh profile after ${label}:`, e);
      });
  } catch (e) {
    console.warn(`[authService] Failed to start profile refresh after ${label}:`, e);
  }
}

/**
 * Install a freshly-minted JWT pair (already returned by the backend, e.g.
 * by /auth/claim-invite/) directly into local storage and rehydrate caches,
 * without making a /auth/login/ round-trip.
 *
 * This is critical for the post-checkout activation path: the backend
 * already issued tokens with the correct `tenant_schema` claim for the
 * newly-provisioned tenant. Round-tripping through /auth/login/ would
 * require us to ferry `tenant_schema` through a URL query param, which
 * is fragile (link-share, browser truncation, etc.) and can silently
 * route the new user into the hostname-default tenant.
 */
async function installSessionFromTokens({ access, refresh, user: profile }) {
  if (!access || !refresh) {
    throw new Error('installSessionFromTokens requires non-empty access and refresh tokens.');
  }

  let decoded = null;
  try {
    decoded = jwtDecode(access);
  } catch {
    decoded = null;
  }

  // Wipe any persisted state from a previous user/tenant before hydrating
  // the new session, so cached responses can't leak across tenants.
  try {
    _clearSharedGetCache();
    _clearPersistentCache();
  } catch {
    // best-effort
  }

  _clearScopedRiskCachesBeforeSessionChange();
  _writeAuthSession({ access, refresh, decoded });
  _notifySessionScopeChangedBestEffort();
  _rememberTenantSchema(decoded?.tenant_schema || decoded?.schema_name);

  if (profile && typeof profile === 'object') {
    _storeCurrentProfile(profile, access);
  }

  // Mark the install moment so the grace-period and warmup logic apply
  // to the post-checkout / claim-invite flow exactly as they do to the
  // standard login flow.
  try { markLoginCompleted(); } catch { /* best-effort */ }

  _refreshProfileInBackground(access, 'token install');
  _rehydrateSyncCacheBestEffort();

  try { kickPostLoginWarmup(); } catch { /* best-effort */ }

  return decoded;
}

export const authService = {
  /**
   * Persist a freshly fetched profile only if it still belongs to the
   * currently stored access token. This prevents an old bootstrap request from
   * overwriting a newly created customer's profile after a session switch.
   */
  storeCurrentProfile(profile, accessToken = '') {
    return _storeCurrentProfile(profile, accessToken);
  },

  /**
   * Login with username and password
   */
  async login(username, password, tenantSchema = '') {
    let response;
    let usedLocalFallback = false;
    const tenantHint = String(tenantSchema || '').trim();
    const loginPayload = {
      username,
      password,
      ...(tenantHint ? { tenant_schema: tenantHint } : {}),
    };
    const loginPayloadWithoutHint = { username, password };
    const loginHeaders = tenantHint
      ? { 'X-Aviatto-Tenant-Schema': tenantHint }
      : undefined;
    try {
      // Login must not inherit stale Authorization headers or local-session
      // routing from a previous browser session. This matters for localhost
      // dist previews, where /api is proxied to the same production API used
      // by the live app.
      response = await _postLoginWithTransientRetry(loginPayload, loginHeaders);
    } catch (err) {
      let primaryErr = err;
      const is401 = err?.response?.status === 401;

      // The browser keeps the last successful tenant_schema as a hint so
      // returning users land in the right tenant quickly. That hint can be
      // stale on shared machines or after switching accounts. If the hinted
      // login fails with 401, retry once without the hint so the backend can
      // run its cross-schema identifier lookup before we tell the operator
      // their valid credentials are wrong.
      if (is401 && tenantHint) {
        try {
          response = await _postLoginWithTransientRetry(loginPayloadWithoutHint, undefined);
        } catch (retryErr) {
          primaryErr = retryErr?.response?.status === 401 ? err : retryErr;
        }
      }

      if (response) {
        // Recovered via no-hint login; skip the localhost fallback branch.
      } else {
        const primaryIs401 = primaryErr?.response?.status === 401;

        // The local-Django fallback below is a developer convenience for
        // accounts created against `python manage.py runserver`. It MUST
        // NOT run in production: a real browser on https://zulbot.com (or
        // any non-localhost origin) cannot connect to 127.0.0.1, so the
        // fallback's network error would replace the real 401 from the
        // remote backend and the Login page would render the generic
        // "Erro ao tentar entrar. Tente novamente em instantes." instead
        // of "wrong username/password" — the exact regression observed
        // post-FRONTEND_URL flip on a Pablo/* registration attempt.
        if (!primaryIs401 || !_isRunningOnLocalhost()) throw primaryErr;

        const LOCAL_BACKEND = 'http://127.0.0.1:8000/api';
        try {
          response = await axios.post(`${LOCAL_BACKEND}/auth/login/`, loginPayload, {
            headers: {
              'Content-Type': 'application/json',
              ...(loginHeaders || {}),
            },
            timeout: LOGIN_REQUEST_TIMEOUT_MS,
          });
          // Mark this session as local-backend so subsequent calls use it too.
          usedLocalFallback = true;
        } catch (localErr) {
          // The fallback didn't help. Always rethrow the ORIGINAL remote
          // 401 so the Login page can show the auth-failure UX (recovery
          // CTA, etc.) instead of leaking a confusing network error from
          // the dev-only fallback URL — even if `localErr` itself has no
          // `.response` (the common case: ERR_CONNECTION_REFUSED).
          throw primaryErr;
        }
      }
    }

    const data = response.data || {};

    // MFA second step: when the backend has ENABLE_MFA on and the account has
    // a factor enrolled, /auth/login/ returns an mfa_required envelope (no
    // tokens) instead of the JWT pair. Surface it to the caller so the Login
    // page can prompt for the code. When MFA is off (production default) this
    // branch never runs and login behaves exactly as before.
    if (data.mfa_required) {
      return {
        mfaRequired: true,
        mfaToken: data.mfa_token,
        methods: Array.isArray(data.methods) ? data.methods : [],
        enrollmentRequired: Boolean(data.enrollment_required),
      };
    }

    const { access, refresh } = data;
    if (!access || !refresh) {
      throw new Error('Login response missing access or refresh token.');
    }
    let decoded = null;
    try {
      decoded = jwtDecode(access);
    } catch {
      decoded = null;
    }

    // Remove all response snapshots belonging to the previous browser
    // session before installing the replacement identity. Cache keys are
    // tenant-scoped as a primary control; clearing also minimizes retained
    // sensitive data on shared workstations.
    _clearSharedGetCache();
    _clearPersistentCache();
    _clearScopedRiskCachesBeforeSessionChange();
    _writeAuthSession({ access, refresh, decoded, localSession: usedLocalFallback });
    _notifySessionScopeChangedBestEffort();
    _rememberTenantSchema(decoded?.tenant_schema || decoded?.schema_name || tenantHint);

    // Record the login moment BEFORE we kick off the warmup or any
    // background fetches. Page-level slow-load banners use this
    // timestamp to suppress false-positive "Conexão lenta" notices
    // during the natural cold-cache window that follows authentication.
    try { markLoginCompleted(); } catch { /* best-effort */ }

    // Re-hydrate the apiClient's synchronous SWR mirror so the very
    // first navigation after login can paint cached data instantly
    // (boot hydration ran before the user identity was known).
    _refreshProfileInBackground(access, 'login');
    _rehydrateSyncCacheBestEffort();

    // Preserve the post-login grace marker. Route chunk/data preloading is
    // handled after the authenticated shell paints, so the operator's first
    // real page request is never queued behind background warmup traffic.
    try { kickPostLoginWarmup(); } catch { /* best-effort */ }

    return response.data;
  },

  /**
   * Register new user
   */
  async register(userData) {
    try {
      const response = await apiClient.post('/users/', userData);
      return response.data;
    } catch (err) {
      // Registration may need the local backend (e.g. after a local Stripe checkout).
      const status = err?.response?.status;
      if (status === 400 || status === 404 || status === 500) {
        const LOCAL_BACKEND = 'http://127.0.0.1:8000/api';
        try {
          const { data } = await axios.post(`${LOCAL_BACKEND}/users/`, userData, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          });
          return data;
        } catch {
          throw err;
        }
      }
      throw err;
    }
  },

  /**
   * Request password reset
   */
  async requestPasswordReset(email) {
    const response = await apiClient.post('/auth/password-reset/', { email });
    return response.data;
  },

  async createAccessSupportRequest(payload) {
    const response = await apiClient.post('/auth/access-support/', payload, {
      __skipAuthRefresh: true,
      __skipAuthHeader: true,
    });
    return response.data;
  },

  /**
   * Reset password with token
   */
  async resetPassword(token, password) {
    const response = await apiClient.post('/auth/password-reset/confirm/', { token, password });
    return response.data;
  },

  /**
   * Install a JWT pair returned directly by the backend (e.g. from
   * /auth/claim-invite/) without re-issuing through /auth/login/.
   * See `installSessionFromTokens` for the rationale.
   */
  installSession: installSessionFromTokens,

  /**
   * Finalize an MFA-gated login: exchange the mfa_token + a verified factor for
   * the JWT pair, then install the session exactly like a normal login.
   * ``method`` is "totp" | "recovery" | "webauthn".
   */
  async completeMfaLogin({ mfaToken, method = 'totp', code = '', response: webauthnResponse = null, stateToken = '' }) {
    const payload = { mfa_token: mfaToken, method };
    if (code) payload.code = code;
    if (webauthnResponse) payload.response = webauthnResponse;
    if (stateToken) payload.state_token = stateToken;

    const resp = await apiClient.post('/auth/mfa/verify/', payload, {
      __skipAuthHeader: true,
      __skipAuthRefresh: true,
    });
    const { access, refresh } = resp.data || {};
    if (!access || !refresh) {
      throw new Error('MFA verification response missing tokens.');
    }
    let decoded = null;
    try { decoded = jwtDecode(access); } catch { decoded = null; }
    _clearSharedGetCache();
    _clearPersistentCache();
    _clearScopedRiskCachesBeforeSessionChange();
    _writeAuthSession({ access, refresh, decoded });
    _notifySessionScopeChangedBestEffort();
    _rememberTenantSchema(decoded?.tenant_schema || decoded?.schema_name);
    try { markLoginCompleted(); } catch { /* best-effort */ }
    _refreshProfileInBackground(access, 'mfa-login');
    _rehydrateSyncCacheBestEffort();
    try { kickPostLoginWarmup(); } catch { /* best-effort */ }
    return resp.data;
  },

  /**
   * Logout - revoke the refresh token server-side (best-effort) then clear
   * local tokens. Local clearing stays synchronous so existing callers (which
   * do not await and immediately redirect) behave exactly as before; the
   * server revoke is fired as a non-blocking best-effort request that
   * blacklists the refresh token so a stolen copy can't be replayed.
   */
  logout() {
    let refreshToken = '';
    try { refreshToken = localStorage.getItem('refresh_token') || ''; } catch { refreshToken = ''; }
    try {
      // Fire-and-forget: never block logout on the network.
      apiClient
        .post(
          '/auth/logout/',
          refreshToken ? { refresh: refreshToken } : {},
          { __skipAuthRefresh: true, __skipRetry: true, __skipAuthHeader: true },
        )
        .catch(() => {});
    } catch {
      // best-effort
    }
    try {
      clearRiskLocalCaches();
    } catch {
      // Risk-cache cleanup must never block logout.
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_profile');
    localStorage.removeItem('aviatto_local_session');
    _notifySessionScopeChangedBestEffort();
    try {
      _clearSharedGetCache();
      _clearPersistentCache();
    } catch {
      // Cache clearing is best-effort; auth storage has already been removed.
    }
    // Drop the post-login grace marker so a public visitor cannot
    // accidentally suppress a slow-load banner with someone else's
    // login timestamp.
    try { clearLoginMark(); } catch { /* best-effort */ }
  },

  getLastTenantSchema() {
    try {
      return String(localStorage.getItem(LAST_TENANT_SCHEMA_KEY) || '').trim();
    } catch {
      return '';
    }
  },

  /**
   * Get current user from token
   */
  getCurrentUser() {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return null;

      const decoded = jwtDecode(token);
      const refreshToken = localStorage.getItem('refresh_token');
      const hasRefreshToken = Boolean(refreshToken);
      const isAccessExpired = Number(decoded?.exp || 0) * 1000 < Date.now();

      // Prefer the stored profile (role-based permissions)
      try {
        const profileRaw = localStorage.getItem('user_profile');
        if (profileRaw) {
          const profile = JSON.parse(profileRaw);
          if (_profileMatchesToken(profile, decoded)) {
            const enriched = _enrichProfileWithTokenScope(profile, decoded);
            if (enriched !== profile) {
              localStorage.setItem('user_profile', JSON.stringify(enriched));
            }
            return enriched;
          }
          if (profile && typeof profile === 'object') {
            localStorage.removeItem('user_profile');
          }
        }
      } catch (e) {
        // Ignore and fallback to decoded token
      }

      if (decoded?.tenant_schema && decoded && typeof decoded === 'object') {
        return {
          ...decoded,
          tenant_schema: decoded.tenant_schema,
        };
      }

      // Legacy fallback for same-schema/dev tokens that predate tenant claims.
      try {
        const profileRaw = localStorage.getItem('user_profile');
        if (profileRaw && !decoded?.tenant_schema) {
          const profile = JSON.parse(profileRaw);
          if (profile && typeof profile === 'object') {
            return profile;
          }
        }
      } catch (e) {
        // Ignore and fallback to decoded token
      }

      if (isAccessExpired && !hasRefreshToken) {
        this.logout();
        return null;
      }

      return decoded;
    } catch (error) {
      console.error('Error getting current user:', error);
      this.logout();
      return null;
    }
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return this.getCurrentUser() !== null;
  },

  /**
   * Get user profile from API
   */
  async getProfile() {
    const response = await apiClient.get('/users/profile/', {
      __skipRetry: true,
      __skipSharedCache: true,
      __skipPersistentCache: true,
      timeout: 10000,
    });
    return response.data;
  },

  /**
   * Get user statistics
   */
  async getStatistics(params = {}) {
    const response = await apiClient.get('/users/statistics/', { params });
    return response.data;
  },
};

export default authService;
